import React, { useState, useEffect, useRef } from "react";
import { Bell, X, Check, ExternalLink } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

interface Notice {
  _id: string;
  title: string;
  content: string;
  type: "urgent" | "important" | "academic" | "info" | "general";
  priority: "high" | "medium" | "low";
  targetAudience: string;
  author: {
    name: string;
    department: string;
  };
  publishDate: string;
  readBy: Array<{ userId: string; readAt: string }>;
  isRead?: boolean;
  metadata?: {
    actionUrl?: string;
    [key: string]: any;
  };
}

interface NotificationDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onToggle,
}) => {
  const { user } = useAuth();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user role for API call
  const userRole = user?.roles?.[0] || "student";

  // Fetch notices
  const fetchNotices = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/api/notices/student?page=1&limit=20`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch notices");
      }

      const data = await response.json();
      // Filter to only unread notices and take the first 5
      const unreadNotices = (data.data || [])
        .filter((notice: Notice) => !notice.isRead)
        .slice(0, 5);
      setNotices(unreadNotices);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load notifications"
      );
    } finally {
      setLoading(false);
    }
  };

  // Mark notice as read
  const markAsRead = async (noticeId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/notices/${noticeId}/read`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        setNotices((prev) => prev.filter((notice) => notice._id !== noticeId));
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/notices/mark-all-read`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        setNotices([]);
      }
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Fetch notices on mount and periodically
  useEffect(() => {
    if (user) {
      fetchNotices(); // Initial fetch on mount

      // Refresh every 30 seconds
      const interval = setInterval(fetchNotices, 30000);

      return () => clearInterval(interval);
    }
  }, [user]);

  // Refresh when dropdown opens
  useEffect(() => {
    if (isOpen && user) {
      fetchNotices();
    }
  }, [isOpen]);

  // Calculate unread count
  const unreadCount = notices.length;

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "border-red-500 bg-red-50";
      case "medium":
        return "border-yellow-500 bg-yellow-50";
      case "low":
        return "border-green-500 bg-green-50";
      default:
        return "border-gray-500 bg-gray-50";
    }
  };

  // Get type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "text-red-600";
      case "important":
        return "text-orange-600";
      case "academic":
        return "text-blue-600";
      case "info":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={onToggle}
        className="relative p-2.5 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 hover:from-blue-100 hover:to-indigo-100 border border-slate-200/60 transition-all duration-300 hover:shadow-md"
      >
        <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px] bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center shadow-md">
            <span className="text-[10px] sm:text-xs text-white font-bold px-1">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-[calc(100vw-2rem)] max-w-80 sm:w-96 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-200/60 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
            <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Notifications
            </h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm bg-gradient-to-r from-blue-500/70 to-indigo-500/70  hover:from-blue-600 hover:to-indigo-600 font-semibold"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={onToggle}
                className="p-1.5 rounded-lg hover:bg-slate-200/60 transition-all duration-200"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-blue-500"></div>
              </div>
            ) : error ? (
              <div className="p-4 text-center">
                <p className="text-sm text-red-600 font-medium">{error}</p>
                <button
                  onClick={fetchNotices}
                  className="mt-2 text-sm bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent hover:from-blue-600 hover:to-indigo-600 font-semibold"
                >
                  Try again
                </button>
              </div>
            ) : notices.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mx-auto mb-3">
                  <Bell className="w-8 h-8 text-slate-500" />
                </div>
                <p className="text-sm text-slate-600 font-medium">
                  No unread notifications
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notices.map((notice) => (
                  <div
                    key={notice._id}
                    className="p-4 hover:bg-gradient-to-br hover:from-slate-50 hover:to-blue-50 transition-all duration-300 bg-gradient-to-br from-blue-50/50 to-indigo-50/50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1.5">
                          <span
                            className={`inline-block w-2 h-2 rounded-full shadow-md ${
                              notice.priority === "high"
                                ? "bg-gradient-to-br from-red-500 to-pink-500"
                                : notice.priority === "medium"
                                ? "bg-gradient-to-br from-yellow-400 to-orange-400"
                                : "bg-gradient-to-br from-emerald-400 to-emerald-500"
                            }`}
                          ></span>
                          <span
                            className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-md shadow-sm ${
                              notice.type === "urgent"
                                ? "bg-gradient-to-r from-red-500 to-pink-500 text-white"
                                : notice.type === "important"
                                ? "bg-gradient-to-r from-orange-400 to-orange-500 text-white"
                                : notice.type === "academic"
                                ? "bg-gradient-to-r from-blue-400 to-blue-500 text-white"
                                : notice.type === "info"
                                ? "bg-gradient-to-r from-green-400 to-emerald-500 text-white"
                                : "bg-gradient-to-r from-slate-300 to-slate-400 text-white"
                            }`}
                          >
                            {notice.type}
                          </span>
                          <span className="text-xs text-slate-500 font-medium">
                            {formatDate(notice.publishDate)}
                          </span>
                        </div>

                        <h4 className="text-sm font-bold text-slate-900 mb-1">
                          {notice.title}
                        </h4>

                        <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed">
                          {notice.content}
                        </p>

                        {notice.metadata?.actionUrl && (
                          <a
                            href={notice.metadata.actionUrl}
                            className="inline-flex items-center text-sm bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent hover:from-blue-600 hover:to-indigo-600 mt-2 font-semibold"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            View Details
                          </a>
                        )}
                      </div>

                      <button
                        onClick={() => markAsRead(notice._id)}
                        className="ml-2 p-1.5 rounded-lg bg-white hover:bg-gradient-to-br hover:from-green-100 hover:to-emerald-100 text-slate-500 hover:text-emerald-600 transition-all duration-300 shadow-sm hover:shadow-md"
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notices.length > 0 && (
            <div className="p-3 border-t border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <button
                onClick={() => {
                  // Navigate to full notifications page (to be implemented)
                  console.log("Navigate to full notifications page");
                }}
                className="w-full text-center text-sm bg-gradient-to-r from-blue-500 to-indigo-500/80 hover:from-blue-600 hover:to-indigo-600/80 font-semibold py-1"
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
